import React, { useEffect, useState, useRef } from 'react';
import styled from '@emotion/native';
import BookingSummary from '@/components/bookConsultation/BookingSummary/BookingSummary';
import {
  SubheaderText,
  Title,
} from '@/components/instantStyling/styledComponents';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import { useMutation } from '@apollo/client';
import {
  MutationFindInstantStylingMatchArgs,
  MutationResult,
  MutationStylistAbsentTimeoutArgs,
} from '@/shared/types/generated';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import { useShallow } from 'zustand/react/shallow';
import BackToTopButton from '@/shared/components/BackToTopButton';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import Spacer from '@/shared/components/Spacer';
import Button from '@/shared/components/Button';
import { BaseText } from '@/shared/components/StyledText';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import { MatchResultCode } from '@/shared/constants/instantCall';
import useExploreStore from '@/stores/useExploreStore';
import { useGetUser } from '@/shared/hooks/useGetUser';
import {
  FIND_INSTANT_STYLING_MATCH,
  STYLIST_ABSENT_TIMEOUT,
} from '@/shared/mutations';

const STYLIST_ABSENT_TIMEOUT_MS = 70000;

interface Props {
  priceTierName: string;
  hourlyRate: number;
  setProgress: React.Dispatch<React.SetStateAction<number>>;
  setHideProgressBar: React.Dispatch<React.SetStateAction<boolean>>;
  setHideContinueButton: React.Dispatch<React.SetStateAction<boolean>>;
  onDismiss: () => void;
}

const ClientFindingMatch = ({
  priceTierName,
  hourlyRate,
  setProgress,
  setHideProgressBar,
  setHideContinueButton,
  onDismiss,
}: Props) => {
  const router = useRouter();
  const scrollToTop = useScrollToTop();

  const { user } = useGetUser();

  const { setSnackbarState, setNoStylistAvailable } = useStore(
    useShallow((state) => ({
      setSnackbarState: state.setSnackbarState,
      setNoStylistAvailable: state.setNoStylistAvailable,
    })),
  );

  const {
    requestForm,
    matchResultCode,
    setMatchResultCode,
    matchedStylist,
    setMatchedStylist,
    matchStatus,
    setMatchStatus,
  } = useInstantStylingStore(
    useShallow((state) => ({
      requestForm: state.requestForm,
      matchResultCode: state.matchResultCode,
      setMatchResultCode: state.setMatchResultCode,
      matchedStylist: state.matchedStylist,
      setMatchedStylist: state.setMatchedStylist,
      matchStatus: state.matchStatus,
      setMatchStatus: state.setMatchStatus,
    })),
  );

  const setSelectedStylist = useExploreStore(
    (state) => state.setSelectedStylist,
  );

  const [isReady, setIsReady] = useState(false);
  const [showNoMatchYet, setShowNoMatchYet] = useState(false);

  // Timer ref for when matched stylist is absent from matchmaking
  const stylistTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [findInstantStylingMatch] = useMutation<
    MutationResult<'findInstantStylingMatch'>,
    MutationFindInstantStylingMatchArgs
  >(FIND_INSTANT_STYLING_MATCH);

  const [stylistAbsentTimeout] = useMutation<
    MutationResult<'stylistAbsentTimeout'>,
    MutationStylistAbsentTimeoutArgs
  >(STYLIST_ABSENT_TIMEOUT);

  // Find styling match (will notify stylist)
  useEffect(() => {
    const fetchMatchingStylist = async () => {
      try {
        if (!matchStatus?.requestId) {
          throw new Error('No request ID found in fetchMatchingStylist');
        }

        const response = await findInstantStylingMatch({
          variables: {
            input: {
              requestId: matchStatus.requestId,
              declinedStylistIds: requestForm.declinedStylistIds,
            },
          },
        });
        const payload = response.data?.findInstantStylingMatch;

        setMatchResultCode(payload?.resultCode ?? null);
        setMatchedStylist(payload?.stylist ?? null);
        setIsReady(true);
      } catch (error) {
        SentryService.captureError('Error finding stylist match', error);
        setSnackbarState({
          open: true,
          message: 'Error finding stylist match. Please try again.',
          severity: 'error',
        });
        setMatchResultCode(null);
        setMatchedStylist(null);
        setIsReady(true);
      }
    };

    fetchMatchingStylist();

    return () => {
      setShowNoMatchYet(false);
      setHideContinueButton(false);
    };
  }, [
    findInstantStylingMatch,
    matchStatus?.requestId,
    requestForm?.declinedStylistIds,
    setHideContinueButton,
    setHideProgressBar,
    setMatchResultCode,
    setMatchedStylist,
    setSnackbarState,
  ]);

  // Handle matched stylist being absent from matchmaking
  useEffect(() => {
    // Clear any existing timer on matchStatus change
    if (stylistTimeoutRef.current) {
      clearTimeout(stylistTimeoutRef.current);
      stylistTimeoutRef.current = null;
    }

    // Start timer only if we have a matched stylist
    if (matchedStylist) {
      stylistTimeoutRef.current = setTimeout(async () => {
        try {
          if (!matchStatus?.requestId || !matchedStylist?.user?.id) {
            throw new Error(
              'Stylist absent timeout: Missing requestId or stylistUserId',
            );
          }

          const { data } = await stylistAbsentTimeout({
            variables: {
              input: {
                requestId: matchStatus?.requestId,
                stylistUserId: matchedStylist?.user?.id,
              },
            },
          });

          if (data?.stylistAbsentTimeout) {
            setMatchStatus(data.stylistAbsentTimeout);
          }
        } catch (error) {
          SentryService.captureError(
            'Error in stylist absent timeout request',
            error,
          );
        }
      }, STYLIST_ABSENT_TIMEOUT_MS);
    }

    return () => {
      if (stylistTimeoutRef.current) {
        clearTimeout(stylistTimeoutRef.current);
        stylistTimeoutRef.current = null;
      }
    };
  }, [matchedStylist, matchStatus, stylistAbsentTimeout, setMatchStatus]);

  // Handle stylist accepted match
  useEffect(() => {
    if (matchStatus?.stylistAccepted && matchedStylist) {
      if (!user?.id || !matchedStylist.user?.id) {
        SentryService.captureError('Matched: Missing user or stylist id');
        return;
      }

      setSelectedStylist(matchedStylist);
      setMatchStatus({
        clientUserId: user.id,
        stylistUserId: matchedStylist.user.id,
      });
      setHideProgressBar(true);
      setProgress((prev) => prev + 1);
    }
  }, [
    matchStatus?.stylistAccepted,
    matchedStylist,
    setHideProgressBar,
    setMatchStatus,
    setProgress,
    setSelectedStylist,
    user?.id,
  ]);

  // Handle match failed
  useEffect(() => {
    if (!isReady) return;

    if (matchResultCode === MatchResultCode.NO_ACTIVE_STYLISTS) {
      onDismiss();
      setNoStylistAvailable(true);
    } else if (matchResultCode === MatchResultCode.NO_STYLISTS_MATCH_CRITERIA) {
      setHideProgressBar(true);
      setHideContinueButton(true);
      setShowNoMatchYet(true);
    }
  }, [
    isReady,
    matchResultCode,
    onDismiss,
    setHideContinueButton,
    setHideProgressBar,
    setNoStylistAvailable,
  ]);

  const onPressAdjustPreferences = () => {
    setProgress(0);
  };

  const onPressBrowseStylists = () => {
    router.push(ROUTES.EXPLORE.path);
    onDismiss();
  };

  const onPressVisitHelpCenter = () => {
    router.push(ROUTES.HELP.path);
    onDismiss();
  };

  if (showNoMatchYet) {
    return (
      <NoMatchYetContainer>
        <Title>No match—just yet.</Title>
        <SubheaderText>
          Try adjusting your preferences to find an instant match, or explore
          all stylists and book a session that works for you
        </SubheaderText>
        <Button
          text="Adjust Preferences"
          onPress={onPressAdjustPreferences}
          customStyle={{ marginBottom: 24 }}
        />
        <Button
          variant="secondary"
          text="Browse Stylists"
          onPress={onPressBrowseStylists}
        />
        <BottomTextContainer>
          <FooterText>
            Have Questions?{' '}
            <FooterLinkText onPress={onPressVisitHelpCenter}>
              Visit Help Center
            </FooterLinkText>
          </FooterText>
        </BottomTextContainer>
      </NoMatchYetContainer>
    );
  }

  return (
    <FindingMatchContainer>
      <SearchingSpinnerContainer>
        <SearchingSpinnerImage
          source={require('@/assets/images/SearchingSpinner.gif')}
        />
      </SearchingSpinnerContainer>
      <Title>{`Finding Your\nStyle Match......`}</Title>
      <SubheaderText>
        {`Sit tight—we're connecting you with\nthe perfect stylist.`}
      </SubheaderText>
      <BookingSummary
        hideStylist
        priceTierName={priceTierName}
        hourlyRate={hourlyRate}
        isInstant
        initialDiscountCode={requestForm?.discountCode}
        initialDiscountPercentage={requestForm?.discountPercentage}
        isPromoCodeLocked
      />
      <Spacer height={42} />
      {/* // TODO (ES-465): Fix back to top button */}
      <BackToTopButton scrollToTop={scrollToTop} />
      <Spacer height={24} />
    </FindingMatchContainer>
  );
};

export default ClientFindingMatch;

const FindingMatchContainer = styled.View({
  flex: 1,
  width: '100%',
  paddingBottom: 56,
});

const SearchingSpinnerContainer = styled.View({
  width: 80,
  height: 80,
  alignSelf: 'center',
});

const SearchingSpinnerImage = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
});

const NoMatchYetContainer = styled.View({
  flex: 1,
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  paddingHorizontal: 30,
  paddingBottom: 24,
});

const BottomTextContainer = styled.View({
  position: 'absolute',
  bottom: 24,
  alignItems: 'center',
});

const FooterText = styled(BaseText)({
  fontSize: 14,
});

const FooterLinkText = styled(BaseText)({
  fontSize: 14,
  fontWeight: 'bold',
  textDecorationLine: 'underline',
});
